import { useEffect, useState } from "react";
import {
  CONNECT_RECORD_COPY,
  RECORDING_RULE_COPY,
  loadRecordingSettings,
  putDepartmentRecordingRule,
  putDepartmentReason,
  putOrganisationRecordingRule,
  putOrganisationReason,
  putTagRecordingRule,
  putTagReason,
  type OrganisationRecordingSetting,
} from "./access.ts";
import { visibleFailure } from "../stepUp.ts";
import { readGroupStealth, writeDepartmentStealth, writeStealthPolicy, writeTagStealth } from "./stealth-api.ts";
import { RecordingPolicyTable, ReasonPolicyTable, type RecordingPolicyRow, type ReasonPolicyRow } from "./settingsPolicyTable.tsx";

function organisationRow(
  rows: OrganisationRecordingSetting[],
  organisationId: string,
): OrganisationRecordingSetting | null {
  return rows.find((item) => item.organisationId === organisationId) ?? null;
}

function departmentRow(org: OrganisationRecordingSetting, departmentId: string) {
  return org.departments.find((item) => item.departmentId === departmentId) ?? null;
}

function tagRow(org: OrganisationRecordingSetting, key: string, value: string) {
  return org.tags.find((item) => item.key === key && item.value === value) ?? null;
}

export const FIXTURE_RECORDING_SETTINGS: OrganisationRecordingSetting[] = [
  {
    organisationId: "00000000-0000-4000-8000-0000000000e1",
    name: "North",
    recordingRule: "Off",
    stealthCommand: false,
    reasonRule: "Off",
    reasonLocked: false,
    departments: [
      {
        departmentId: "00000000-0000-4000-8000-0000000000e2",
        name: "Desk",
        recordingRule: "Inherit",
        effectiveRule: "Off",
        locked: false,
        stealthCommand: false,
        reasonRule: "Inherit",
        reasonEffective: "Off",
        reasonLocked: false,
      },
    ],
    tags: [
      {
        key: "site",
        value: "leeds",
        recordingRule: "Allowed",
        effectiveRule: "Allowed",
        locked: false,
        stealthCommand: false,
        reasonRule: "Inherit",
        reasonEffective: "Off",
        reasonLocked: false,
      },
    ],
  },
  {
    organisationId: "00000000-0000-4000-8000-0000000000e3",
    name: "South",
    recordingRule: "Required",
    stealthCommand: false,
    reasonRule: "Required",
    reasonLocked: true,
    departments: [
      {
        departmentId: "00000000-0000-4000-8000-0000000000e4",
        name: "IT Support",
        recordingRule: "Inherit",
        effectiveRule: "Required",
        locked: true,
        stealthCommand: false,
        reasonRule: "Inherit",
        reasonEffective: "Required",
        reasonLocked: true,
      },
      {
        departmentId: "00000000-0000-4000-8000-0000000000e5",
        name: "Testing",
        recordingRule: "Inherit",
        effectiveRule: "Required",
        locked: true,
        stealthCommand: false,
        reasonRule: "Inherit",
        reasonEffective: "Required",
        reasonLocked: true,
      },
    ],
    tags: [],
  },
];

export const RECORDING_RULES = ["Off", "Allowed", "Required"] as const;

export function ruleWord(rule: string): string {
  return rule === "Allowed" ? "Optional" : rule;
}

export function recordingChoiceLabel(choice: string, effective: string, locked: boolean): string {
  if (locked) return "required by organisation";
  return `${ruleWord(choice)} (${ruleWord(effective)})`;
}

export type RecordingTarget =
  | { kind: "organisation" }
  | { kind: "department"; departmentId: string }
  | { kind: "tag"; key: string; value: string };

function ruleRank(rule: string): number {
  if (rule === "Required") return 2;
  if (rule === "Allowed") return 1;
  return 0;
}

function rowEffective(stored: string, organisationRule: string, locked: boolean): string {
  if (locked) return "Required";
  if (stored === "Inherit") return organisationRule;
  return ruleRank(stored) >= ruleRank(organisationRule) ? stored : organisationRule;
}

export function previewRecordingSettings(
  rows: OrganisationRecordingSetting[],
  organisationId: string,
  target: RecordingTarget,
  choice: string,
): OrganisationRecordingSetting[] {
  return rows.map((org) => {
    if (org.organisationId !== organisationId) return org;
    const organisationRule = target.kind === "organisation" ? choice : org.recordingRule;
    const locked = organisationRule === "Required";
    return {
      ...org,
      recordingRule: organisationRule,
      departments: org.departments.map((department) => {
        const stored = target.kind === "department" && department.departmentId === target.departmentId ? choice : department.recordingRule;
        return { ...department, recordingRule: stored, effectiveRule: rowEffective(stored, organisationRule, locked), locked };
      }),
      tags: org.tags.map((tag) => {
        const stored = target.kind === "tag" && tag.key === target.key && tag.value === target.value ? choice : tag.recordingRule;
        return { ...tag, recordingRule: stored, effectiveRule: rowEffective(stored, organisationRule, locked), locked };
      }),
    };
  });
}

export function previewStealth(
  rows: OrganisationRecordingSetting[],
  organisationId: string,
  target: RecordingTarget,
  enabled: boolean,
): OrganisationRecordingSetting[] {
  return rows.map((org) => {
    if (org.organisationId !== organisationId) return org;
    return {
      ...org,
      stealthCommand: target.kind === "organisation" ? enabled : org.stealthCommand,
      departments: org.departments.map((department) =>
        target.kind === "department" && department.departmentId === target.departmentId
          ? { ...department, stealthCommand: enabled }
          : department,
      ),
      tags: org.tags.map((tag) =>
        target.kind === "tag" && tag.key === target.key && tag.value === target.value ? { ...tag, stealthCommand: enabled } : tag,
      ),
    };
  });
}

export function previewReason(
  rows: OrganisationRecordingSetting[],
  organisationId: string,
  target: RecordingTarget,
  choice: string,
): OrganisationRecordingSetting[] {
  return rows.map((org) => {
    if (org.organisationId !== organisationId) return org;
    const organisationRule = target.kind === "organisation" ? choice : org.reasonRule;
    const locked = organisationRule === "Required";
    const effective = (stored: string) => (locked || stored === "Required" ? "Required" : "Off");
    return {
      ...org,
      reasonRule: organisationRule,
      reasonLocked: locked,
      departments: org.departments.map((department) => {
        const stored = target.kind === "department" && department.departmentId === target.departmentId ? choice : department.reasonRule;
        return { ...department, reasonRule: stored, reasonEffective: effective(stored), reasonLocked: locked };
      }),
      tags: org.tags.map((tag) => {
        const stored = target.kind === "tag" && tag.key === target.key && tag.value === target.value ? choice : tag.reasonRule;
        return { ...tag, reasonRule: stored, reasonEffective: effective(stored), reasonLocked: locked };
      }),
    };
  });
}

export function RecordingPage(props: { apiBase: string; token: () => Promise<string> }) {
  const fixture = import.meta.env.DEV && new URLSearchParams(window.location.search).get("wyloVerify") === "recording";
  const [rows, setRows] = useState<OrganisationRecordingSetting[] | null>(fixture ? FIXTURE_RECORDING_SETTINGS : null);
  const [error, setError] = useState<string | null>(null);
  const [showStealth, setShowStealth] = useState(fixture);
  useEffect(() => {
    if (fixture) return;
    const controller = new AbortController();
    void loadRecordingSettings(props.apiBase, props.token, controller.signal).then(async (result) => {
      if (controller.signal.aborted) return;
      let next = result.rows;
      let visible = false;
      for (const row of result.rows) {
        const stealth = await readGroupStealth(props.apiBase, props.token, row.organisationId);
        if (controller.signal.aborted) return;
        if (!stealth) continue;
        visible = true;
        next = previewStealth(next, row.organisationId, { kind: "organisation" }, stealth.enabled);
        for (const department of stealth.departments) {
          next = previewStealth(next, row.organisationId, { kind: "department", departmentId: department.departmentId }, department.enabled);
        }
        for (const tag of stealth.tags) {
          next = previewStealth(next, row.organisationId, { kind: "tag", key: tag.key, value: tag.value }, tag.enabled);
        }
      }
      setRows(next);
      setShowStealth(visible);
      setError(result.error);
    }).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      const message = visibleFailure(cause, "Recordings could not be loaded.");
      if (message) setError(message);
    });
    return () => controller.abort();
  }, [fixture, props.apiBase, props.token]);

  async function reload() {
    try {
      const result = await loadRecordingSettings(props.apiBase, props.token, new AbortController().signal);
      setRows(result.rows);
      setError(result.error);
    } catch (cause) {
      const message = visibleFailure(cause, "Recordings could not be loaded.");
      if (message) setError(message);
    }
  }

  return (
    <RecordingSettings
      rows={rows ?? []}
      pending={rows === null}
      error={error}
      showStealth={showStealth}
      onRecording={(organisationId, target, recordingRule) => {
        if (fixture) {
          setRows((current) => previewRecordingSettings(current ?? [], organisationId, target, recordingRule));
          return;
        }
        const save =
          target.kind === "organisation"
            ? putOrganisationRecordingRule(props.apiBase, props.token, organisationId, recordingRule)
            : target.kind === "department"
              ? putDepartmentRecordingRule(props.apiBase, props.token, organisationId, target.departmentId, recordingRule)
              : putTagRecordingRule(props.apiBase, props.token, organisationId, target.key, target.value, recordingRule);
        void save
          .then((detail) => {
            if (detail) setError(detail);
            else void reload();
          })
          .catch((cause: unknown) => {
            const message = visibleFailure(cause, "That change could not be saved.");
            if (message) setError(message);
          });
      }}
      onReason={(organisationId, target, reasonRequired) => {
        if (fixture) {
          setRows((current) => previewReason(current ?? [], organisationId, target, reasonRequired));
          return;
        }
        const save =
          target.kind === "organisation"
            ? putOrganisationReason(props.apiBase, props.token, organisationId, reasonRequired)
            : target.kind === "department"
              ? putDepartmentReason(props.apiBase, props.token, organisationId, target.departmentId, reasonRequired)
              : putTagReason(props.apiBase, props.token, organisationId, target.key, target.value, reasonRequired);
        void save
          .then((detail) => {
            if (detail) setError(detail);
            else void reload();
          })
          .catch((cause: unknown) => {
            const message = visibleFailure(cause, "That change could not be saved.");
            if (message) setError(message);
          });
      }}
      onStealth={(organisationId, target, enabled) => {
        if (fixture) {
          setRows((current) => previewStealth(current ?? [], organisationId, target, enabled));
          return;
        }
        const save =
          target.kind === "organisation"
            ? writeStealthPolicy(props.apiBase, props.token, organisationId, enabled)
            : target.kind === "department"
              ? writeDepartmentStealth(props.apiBase, props.token, organisationId, target.departmentId, enabled)
              : writeTagStealth(props.apiBase, props.token, organisationId, target.key, target.value, enabled);
        void save
          .then((detail) => {
            if (detail) setError(detail);
            else setRows((current) => previewStealth(current ?? [], organisationId, target, enabled));
          })
          .catch((cause: unknown) => {
            const message = visibleFailure(cause, "That change could not be saved.");
            if (message) setError(message);
          });
      }}
    />
  );
}

function RuleSelect(props: {
  label: string;
  value: string;
  inherit: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <select aria-label={props.label} value={props.value} disabled={props.disabled} onChange={(event) => props.onChange(event.target.value)}>
      {props.inherit ? <option value="Inherit">Inherit</option> : null}
      {RECORDING_RULES.map((rule) => (
        <option key={rule} value={rule}>
          {ruleWord(rule)}
        </option>
      ))}
    </select>
  );
}

export function RecordingSettings(props: {
  rows: OrganisationRecordingSetting[];
  pending?: boolean;
  error: string | null;
  showStealth?: boolean;
  onRecording: (organisationId: string, target: RecordingTarget, recordingRule: string) => void;
  onReason: (organisationId: string, target: RecordingTarget, reasonRequired: string) => void;
  onStealth?: (organisationId: string, target: RecordingTarget, enabled: boolean) => void;
}) {
  return (
    <section className="panel">
      <header className="panel-head">
        <div>
          <h2>Policies</h2>
          <h3>Recording</h3>
          <div className="groups-help" aria-label="Recording rules">
            <p>
              <strong>Off.</strong> Nobody can record. A request to record is rejected.
            </p>
            <p>
              <strong>Allowed.</strong> The operator chooses for that session. Attended and Unattended show a Record checkbox, unticked, and the choice is not kept. devices.connect on that device shows that checkbox and sends the choice. Stealth Command records a transcript every time and shows no checkbox.
            </p>
            <p>
              <strong>Required.</strong> Every session records. The Record checkbox is ticked and disabled, and the server turns recording on. A locked department or tag select shows Required and stays disabled.
            </p>
            <p>
              The device rule is the strictest of the organisation and the department. Required beats Allowed beats Off. Inherit keeps the organisation rule. A weaker department choice cannot lower it, so organisation Required forces every department. Each tag the device carries joins the same way when that tag sets Off, Allowed, or Required. Inherit does not join, and a tag the device does not carry is ignored.
            </p>
            <p>
              {RECORDING_RULE_COPY} {CONNECT_RECORD_COPY}
            </p>
          </div>
        </div>
      </header>
      {props.error ? <p role="alert">{props.error}</p> : null}
      {props.pending ? <p>Loading recordings.</p> : null}
      {!props.pending && props.rows.length === 0 && !props.error ? <p>No organisations.</p> : null}
      {props.rows.length > 0 ? (
        <RecordingPolicyTable
          rows={props.rows}
          showStealth={props.showStealth}
          renderRecording={(row) => {
            const org = organisationRow(props.rows, row.organisationId);
            if (!org) return null;
            const target = row.target;
            if (target.kind === "organisation") {
              return (
                <RuleSelect
                  label={`${org.name} recording`}
                  value={org.recordingRule}
                  inherit={false}
                  onChange={(value) => props.onRecording(org.organisationId, { kind: "organisation" }, value)}
                />
              );
            }
            if (target.kind === "department") {
              const department = departmentRow(org, target.departmentId);
              if (!department) return null;
              return (
                <RuleSelect
                  label={`${department.name} recording`}
                  value={department.locked ? "Required" : department.recordingRule}
                  inherit
                  disabled={department.locked}
                  onChange={(value) => props.onRecording(org.organisationId, { kind: "department", departmentId: department.departmentId }, value)}
                />
              );
            }
            const tag = tagRow(org, target.key, target.value);
            if (!tag) return null;
            return (
              <RuleSelect
                label={`${row.name} recording`}
                value={tag.locked ? "Required" : tag.recordingRule}
                inherit
                disabled={tag.locked}
                onChange={(value) => props.onRecording(org.organisationId, { kind: "tag", key: tag.key, value: tag.value }, value)}
              />
            );
          }}
          renderStealth={
            props.showStealth
              ? (row: RecordingPolicyRow) => {
                  const org = organisationRow(props.rows, row.organisationId);
                  if (!org) return null;
                  const target = row.target;
                  if (target.kind === "organisation") {
                    return (
                      <input
                        type="checkbox"
                        aria-label={`${org.name} Stealth Command`}
                        checked={org.stealthCommand}
                        onChange={(event) => props.onStealth?.(org.organisationId, { kind: "organisation" }, event.target.checked)}
                      />
                    );
                  }
                  if (target.kind === "department") {
                    const department = departmentRow(org, target.departmentId);
                    if (!department) return null;
                    return (
                      <input
                        type="checkbox"
                        aria-label={`${department.name} Stealth Command`}
                        checked={department.stealthCommand}
                        onChange={(event) =>
                          props.onStealth?.(org.organisationId, { kind: "department", departmentId: department.departmentId }, event.target.checked)
                        }
                      />
                    );
                  }
                  const tag = tagRow(org, target.key, target.value);
                  if (!tag) return null;
                  return (
                    <input
                      type="checkbox"
                      aria-label={`${row.name} Stealth Command`}
                      checked={tag.stealthCommand}
                      onChange={(event) => props.onStealth?.(org.organisationId, { kind: "tag", key: tag.key, value: tag.value }, event.target.checked)}
                    />
                  );
                }
              : undefined
          }
        />
      ) : null}
      <h3>Required reason</h3>
      <p>
        Remote Control, Stealth Control, and Stealth Command ask for a reason. It is required only where this policy says so. Off is the default when no organisation, department, or tag requires it. The strictest of those scopes wins. A department or tag cannot turn off an organisation requirement.
      </p>
      {props.rows.length > 0 ? (
        <ReasonPolicyTable
          rows={props.rows}
          renderReason={(row: ReasonPolicyRow) => {
            const org = organisationRow(props.rows, row.organisationId);
            if (!org) return null;
            const target = row.target;
            if (target.kind === "organisation") {
              return (
                <ReasonSelect
                  label={`${org.name} reason`}
                  value={org.reasonRule}
                  inherit={false}
                  locked={false}
                  onChange={(value) => props.onReason(org.organisationId, { kind: "organisation" }, value)}
                />
              );
            }
            if (target.kind === "department") {
              const department = departmentRow(org, target.departmentId);
              if (!department) return null;
              return (
                <ReasonSelect
                  label={`${department.name} reason`}
                  value={department.reasonLocked && department.reasonRule === "Off" ? "Inherit" : department.reasonRule}
                  inherit
                  locked={department.reasonLocked}
                  onChange={(value) => props.onReason(org.organisationId, { kind: "department", departmentId: department.departmentId }, value)}
                />
              );
            }
            const tag = tagRow(org, target.key, target.value);
            if (!tag) return null;
            return (
              <ReasonSelect
                label={`${row.name} reason`}
                value={tag.reasonLocked && tag.reasonRule === "Off" ? "Inherit" : tag.reasonRule}
                inherit
                locked={tag.reasonLocked}
                onChange={(value) => props.onReason(org.organisationId, { kind: "tag", key: tag.key, value: tag.value }, value)}
              />
            );
          }}
        />
      ) : null}
    </section>
  );
}

function ReasonSelect(props: {
  label: string;
  value: string;
  inherit: boolean;
  locked: boolean;
  onChange: (value: string) => void;
}) {
  const choices = props.inherit ? (props.locked ? ["Inherit", "Required"] : ["Inherit", "Off", "Required"]) : ["Off", "Required"];
  return (
    <select aria-label={props.label} value={props.value} onChange={(event) => props.onChange(event.target.value)}>
      {choices.map((choice) => (
        <option key={choice} value={choice}>
          {choice}
        </option>
      ))}
    </select>
  );
}
